import { isMobile } from '../../utils/check-media'
import { debounce } from '../../utils/debounce'
import { BaseComponent } from '../../components/base-component'
import { ModalComponent } from 'src/scripts/components/modal'
import { $el, $list, $elParent, isNotThemeStore } from 'src/scripts/utils/utils'
import { SliderGrid } from 'src/scripts/components/carousel/slider-grid'

const PIN_SELECTOR = '[data-lookbook-pin]'
const BLOCK_SELECTOR = '[data-lookbook-block]'
const MODAL_SELECTOR = '[data-lookbook-modal]'
const CAROUSEL_SELECTOR = 'slider-grid:not([with-grid])'
const CAROUSEL_TRACK_SELECTOR =
  '[data-lookbook-mobile-content] [slider-grid-track]'
const DESKTOP_CONTENT_SELECTOR = '[data-lookbook-desktop-content]'
const MOBILE_CONTENT_SELECTOR = '[data-lookbook-mobile-content]'

export class LookbookComponent extends BaseComponent {
  animationPausedAttribute: string = 'animation-paused'
  selectedPinId: string
  notHighlightedAttribute: string = 'not-highlighted'
  pins: HTMLElement[]
  highlightedBlockId: string
  resizeObserver: ResizeObserver
  carouselTrack: HTMLElement

  mountComponent(): void {
    const pins = $list(PIN_SELECTOR, this)

    pins.forEach(pin => {
      this.addListener(pin, 'mouseenter', this.handlePinEnter)
      this.addListener(pin, 'mouseleave', this.handlePinLeave)
      this.addListener(pin, 'click', this.handlePinClick)
    })

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)

    if (isMobile()) {
      this.carouselTrack = $el(CAROUSEL_TRACK_SELECTOR, this)
    }
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()
  }

  handlePinEnter = (): void => {
    this.setAnimationPausedState(true)
  }

  handlePinLeave = (): void => {
    if (this.highlightedBlockId) {
      return
    }

    this.setAnimationPausedState(false)
  }

  handleResize = debounce(() => {
    this.showBlockById(this.highlightedBlockId)
  }, 500)

  handlePinClick = (event: MouseEvent): void => {
    const pin = event.target as HTMLElement
    const blockId = pin.dataset.blockId

    this.selectedPinId = blockId

    if (pin.parentElement.tagName !== 'MODAL-BUTTON') {
      this.showBlockById(blockId)
    }

    this.setListeners()
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const pin = $elParent(PIN_SELECTOR, event.target)

    if (!pin) {
      this.showBlockById()
      this.removeListeners()
    }
  }

  handlePointerMove = () => {
    this.showBlockById()
    this.removeListeners()
  }

  setListeners = () => {
    this.addListener(window, 'click', this.handleOutsideClick)

    if (this.carouselTrack) {
      this.addListener(
        this.carouselTrack,
        'pointermove',
        this.handlePointerMove,
      )
    }
  }

  removeListeners = () => {
    this.removeListener(window, 'click', this.handleOutsideClick)

    if (this.carouselTrack) {
      this.removeListener(
        this.carouselTrack,
        'pointermove',
        this.handlePointerMove,
      )
    }
  }

  showBlockById(blockId?: string): void {
    this.updateSelectedStateForPins(blockId)

    if (blockId) {
      this.setHighlightBlockById(blockId)
      this.setAnimationPausedState(true)
    } else {
      this.resetHighlight()
      this.setAnimationPausedState(false)
    }

    this.highlightedBlockId = blockId
  }

  updateSelectedStateForPins(blockId?: string) {
    const pins = $list(PIN_SELECTOR, this)
    pins.forEach(pin => {
      pin.toggleAttribute('is-selected', pin.dataset.blockId === blockId)
    })
  }

  setAnimationPausedState(isAnimationPaused: boolean): void {
    this.toggleAttribute(this.animationPausedAttribute, isAnimationPaused)
  }

  setHighlightBlockById(blockId?: string): void {
    this.blocks.forEach((block, index) => {
      const isHighlighted = block.dataset.blockId === blockId

      if (isHighlighted) {
        this.scrollToProductByIndex(index)
      }

      block.removeAttribute('style')
      block.toggleAttribute(this.notHighlightedAttribute, !isHighlighted)
    })
  }

  scrollToProductByIndex(index: number) {
    const block = this.blocks[index]
    const carousel: SliderGrid = $elParent(CAROUSEL_SELECTOR, block)

    if (carousel) {
      carousel.scrollSlideIntoView(index)
    } else if (isNotThemeStore()) {
      block.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  resetHighlight() {
    this.blocks.forEach(block => {
      block.removeAttribute(this.notHighlightedAttribute)
    })
  }

  get blocks(): HTMLElement[] {
    const desktopContent = $el(DESKTOP_CONTENT_SELECTOR, this)
    const mobileContent = $el(MOBILE_CONTENT_SELECTOR, this)
    const content = isMobile() ? mobileContent : desktopContent
    const modal: ModalComponent = $el(MODAL_SELECTOR, content)
    const node = modal ? modal.element : content

    return $list(BLOCK_SELECTOR, node)
  }
}
